import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Grid,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardHeader,
  InputAdornment,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

function Settings() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/settings');
      const settingsData = response.data.settings;
      setSettings(settingsData);

      // Initialize form values
      const initialValues = {};
      settingsData.forEach(setting => {
        initialValues[setting.setting_key] = setting.setting_value;
      });
      setFormValues(initialValues);
      setLoading(false);
    } catch (err) {
      setError('Failed to load settings');
      setLoading(false);
      console.error('Settings fetch error:', err);
    }
  };

  const handleChange = (key, value) => {
    setFormValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // Prepare settings data for API
      const settingsToUpdate = Object.keys(formValues).map(key => ({
        key,
        value: formValues[key]
      }));

      const response = await axios.post('/settings/update', {
        settings: settingsToUpdate
      });

      if (response.data.success) {
        showNotification('Settings saved successfully', 'success');
        fetchSettings(); // Refresh settings
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      showNotification('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetForm = () => {
    // Reset to current server values
    const initialValues = {};
    settings.forEach(setting => {
      initialValues[setting.setting_key] = setting.setting_value;
    });
    setFormValues(initialValues);
    showNotification('Form reset to saved values', 'info');
  };

  const showNotification = (message, severity) => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const getSettingByKey = (key) => {
    return settings.find(setting => setting.setting_key === key);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Application Settings
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            General Settings
          </Typography>
          <Box>
            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleResetForm}
              sx={{ mr: 2 }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              disabled={saving}
            >
              Save Changes
              {saving && <CircularProgress size={24} sx={{ ml: 1 }} />}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardHeader title="Deposit Requirements" />
              <CardContent>
                {/* Deposit Required Radio Buttons (Yes/No) */}
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel component="legend">Require Deposit for Calls</FormLabel>
                  <RadioGroup
                    row
                    name="deposit_required"
                    value={formValues.deposit_required || 'no'}
                    onChange={(e) => handleChange('deposit_required', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  <Typography variant="body2" color="text.secondary">
                    If set to Yes, users must have a deposit to make calls
                  </Typography>
                </FormControl>

                {/* Allow Calls Without Deposit Radio Buttons */}
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}
                  disabled={formValues.deposit_required !== 'yes'}
                >
                  <FormLabel component="legend">Allow Calls Without Deposit</FormLabel>
                  <RadioGroup
                    row
                    name="call_without_deposit_allowed"
                    value={formValues.call_without_deposit_allowed || 'no'}
                    onChange={(e) => handleChange('call_without_deposit_allowed', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  <Typography variant="body2" color="text.secondary">
                    If set to Yes, users can make calls even without deposit (override)
                  </Typography>
                </FormControl>

                <TextField
                  label="Minimum Deposit Amount"
                  fullWidth
                  type="number"
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  value={formValues.minimum_deposit || 0}
                  onChange={(e) => handleChange('minimum_deposit', parseFloat(e.target.value))}
                  disabled={formValues.deposit_required !== 'yes'}
                />
                
                <TextField
                  label="Deposit Warning Threshold"
                  fullWidth
                  type="number"
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  value={formValues.deposit_warning_threshold || 0}
                  onChange={(e) => handleChange('deposit_warning_threshold', parseFloat(e.target.value))}
                  disabled={formValues.deposit_required !== 'yes'}
                  helperText="Send warning when deposit falls below this amount"
                />
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardHeader title="Balance Settings" />
              <CardContent>
                <TextField
                  label="Low Balance Alert Threshold"
                  fullWidth
                  type="number"
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  value={formValues.low_balance_threshold || 0}
                  onChange={(e) => handleChange('low_balance_threshold', parseFloat(e.target.value))}
                  helperText="Users will receive alerts when balance falls below this amount"
                />

                <Box sx={{ mt: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formValues.enable_auto_recharge === true}
                        onChange={(e) => handleChange('enable_auto_recharge', e.target.checked)}
                      />
                    }
                    label="Enable Auto Recharge"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Automatically recharge when balance falls below threshold
                  </Typography>
                </Box>

                <TextField
                  label="Default Recharge Amount"
                  fullWidth
                  type="number"
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  value={formValues.default_recharge_amount || 0}
                  onChange={(e) => handleChange('default_recharge_amount', parseFloat(e.target.value))}
                  disabled={formValues.enable_auto_recharge !== true}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <Typography variant="h6" gutterBottom>
          Withdrawal Settings
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardHeader title="Withdrawal Configuration" />
              <CardContent>
                {/* Enable/Disable Withdrawals */}
                <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                  <FormLabel component="legend">Enable Withdrawals</FormLabel>
                  <RadioGroup
                    row
                    name="withdrawal_enabled"
                    value={formValues.withdrawal_enabled || 'no'}
                    onChange={(e) => handleChange('withdrawal_enabled', e.target.value)}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  <Typography variant="body2" color="text.secondary">
                    Enable or disable the withdrawal functionality for users
                  </Typography>
                </FormControl>
                
                <TextField
                  label="Minimum Withdrawal Amount"
                  fullWidth
                  type="number"
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  value={formValues.min_withdrawal_amount || 10}
                  onChange={(e) => handleChange('min_withdrawal_amount', parseFloat(e.target.value))}
                  disabled={formValues.withdrawal_enabled !== 'yes'}
                  helperText="Minimum amount users can withdraw"
                />
                
                <TextField
                  label="Maximum Withdrawal Amount"
                  fullWidth
                  type="number"
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                  }}
                  value={formValues.max_withdrawal_amount || 5000}
                  onChange={(e) => handleChange('max_withdrawal_amount', parseFloat(e.target.value))}
                  disabled={formValues.withdrawal_enabled !== 'yes'}
                  helperText="Maximum amount users can withdraw at once"
                />
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardHeader title="Withdrawal Processing" />
              <CardContent>
                <TextField
                  label="Withdrawal Fee Percentage"
                  fullWidth
                  type="number"
                  margin="normal"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">%</InputAdornment>,
                  }}
                  value={formValues.withdrawal_fee_percentage || 2.5}
                  onChange={(e) => handleChange('withdrawal_fee_percentage', parseFloat(e.target.value))}
                  disabled={formValues.withdrawal_enabled !== 'yes'}
                  helperText="Fee percentage charged on withdrawals"
                />
                
                <TextField
                  label="Processing Time"
                  fullWidth
                  margin="normal"
                  value={formValues.withdrawal_processing_time || '1-3 business days'}
                  onChange={(e) => handleChange('withdrawal_processing_time', e.target.value)}
                  disabled={formValues.withdrawal_enabled !== 'yes'}
                  helperText="Typical time it takes to process withdrawals (shown to users)"
                />
                
                <FormControl component="fieldset" sx={{ mt: 3, width: '100%' }}>
                  <FormLabel component="legend">Auto-Approve Withdrawals</FormLabel>
                  <RadioGroup
                    row
                    name="auto_approve_withdrawals"
                    value={formValues.auto_approve_withdrawals || 'no'}
                    onChange={(e) => handleChange('auto_approve_withdrawals', e.target.value)}
                    disabled={formValues.withdrawal_enabled !== 'yes'}
                  >
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                  <Typography variant="body2" color="text.secondary">
                    Automatically approve withdrawal requests (use with caution)
                  </Typography>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Settings;